import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, Upload, Download, ShieldAlert, Loader2, Globe2, Trash2, ArrowLeft } from 'lucide-react';

const LANGS = ['shona','ndebele','yoruba','swahili','french','portuguese','spanish','zulu','xhosa','amharic','hausa','igbo','chichewa','afrikaans'];

/**
 * /admin/countries — super-admin tool for managing country records and bulk-
 * uploading the per-country Excel files (Facts + Quiz sheets).
 */
export default function AdminCountries() {
    const { user } = useAuth();
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get('/admin/countries');
            setCountries(data.countries || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not load countries');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    if (!user) return <main className="max-w-4xl mx-auto px-6 py-16">Sign in.</main>;
    if (user.role !== 'admin') return <main className="max-w-4xl mx-auto px-6 py-16">Admin only.</main>;

    return (
        <main className="max-w-6xl mx-auto px-6 py-10" data-testid="page-admin-countries">
            <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-lq-ink-500 hover:text-lq-purple-700"><ArrowLeft size={14}/> Back to admin</Link>
            <p className="mt-3 text-sm uppercase tracking-wider text-lq-purple-500 font-semibold inline-flex items-center gap-1"><ShieldAlert size={14}/> Admin</p>
            <h1 className="mt-1 font-display text-3xl md:text-4xl font-semibold text-lq-ink-black">Countries — facts &amp; quizzes</h1>
            <p className="mt-2 text-lq-ink-700 max-w-3xl">
                Each country has its own Excel workbook with <strong>Facts</strong> and <strong>Quiz</strong> sheets.
                Create or update the country record below, then upload the workbook. Re-uploading replaces the country's facts and quiz questions.
            </p>

            <TemplateDownloader />

            <NewCountryForm onCreated={refresh} />

            <EmptyCountriesWarning countries={countries} />

            <h2 className="mt-12 font-display text-xl font-semibold text-lq-ink-900 inline-flex items-center gap-2"><Globe2 size={18}/> Existing countries</h2>
            {loading && <p className="mt-3 text-lq-ink-500">Loading…</p>}
            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {countries.map((c) => <CountryAdminCard key={c.slug} c={c} onChange={refresh} />)}
            </div>
        </main>
    );
}

function EmptyCountriesWarning({ countries }) {
    if (!countries || countries.length === 0) return null;
    const empty = countries.filter((c) => (c.fact_count || 0) === 0 && (c.quiz_question_count || 0) === 0);
    if (empty.length === 0) return null;
    return (
        <div className="mt-6 bg-lq-peach-100 border border-lq-peach-300 rounded-2xl p-5" data-testid="empty-countries-warning">
            <h3 className="font-display text-base font-semibold text-lq-ink-900 inline-flex items-center gap-2">
                <ShieldAlert size={16} className="text-lq-peach-600" />
                {empty.length} {empty.length === 1 ? 'country needs' : 'countries need'} content
            </h3>
            <p className="mt-1 text-sm text-lq-ink-700">
                These countries are created but have no facts or quiz questions yet, so they&rsquo;re hidden from the public Countries page until you upload the Excel workbook.
            </p>
            <ul className="mt-3 flex flex-wrap gap-1.5" data-testid="empty-countries-list">
                {empty.map((c) => (
                    <li key={c.slug} className="text-xs px-2 py-0.5 rounded-full bg-white border border-lq-peach-300 text-lq-ink-900 inline-flex items-center gap-1">
                        <span aria-hidden>{c.flag_emoji}</span>{c.name}
                    </li>
                ))}
            </ul>
        </div>
    );
}

function TemplateDownloader() {
    const [busy, setBusy] = useState(false);
    const download = async () => {
        setBusy(true);
        try {
            const { data } = await api.get('/admin/countries/excel-template');
            const bin = atob(data.content_b64);
            const bytes = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
            const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = data.filename || 'country-template.xlsx';
            a.click(); URL.revokeObjectURL(url);
            toast.success('Template downloaded');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not download');
        } finally { setBusy(false); }
    };
    return (
        <div className="mt-5 bg-lq-purple-50 border border-lq-purple-100 rounded-xl px-5 py-4 flex items-center justify-between flex-wrap gap-3">
            <div>
                <h3 className="font-semibold text-lq-ink-900">Excel template</h3>
                <p className="text-sm text-lq-ink-700">Download a pre-filled template with example rows for both sheets.</p>
            </div>
            <button onClick={download} disabled={busy} className="inline-flex items-center gap-1 px-5 py-2 rounded-full bg-white border border-lq-purple-300 text-lq-purple-700 font-semibold hover:bg-lq-purple-100 disabled:opacity-50" data-testid="admin-template-download">
                {busy ? <Loader2 size={14} className="animate-spin"/> : <Download size={14}/>} Download template
            </button>
        </div>
    );
}

function NewCountryForm({ onCreated }) {
    const [form, setForm] = useState({
        slug: '', name: '', flag_emoji: '', country_code: '', region: '',
        language_slugs: [], status: 'live', quiz_status: 'coming_soon', blurb: '',
    });
    const [busy, setBusy] = useState(false);

    const toggleLang = (s) => setForm((f) => ({
        ...f,
        language_slugs: f.language_slugs.includes(s)
            ? f.language_slugs.filter((x) => x !== s)
            : [...f.language_slugs, s],
    }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.flag_emoji) { toast.error('Name and flag are required'); return; }
        setBusy(true);
        try {
            await api.post('/admin/countries', form);
            toast.success(`Country saved`);
            setForm({ slug: '', name: '', flag_emoji: '', country_code: '', region: '', language_slugs: [], status: 'live', quiz_status: 'coming_soon', blurb: '' });
            await onCreated?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not save');
        } finally { setBusy(false); }
    };

    return (
        <form onSubmit={submit} className="mt-6 bg-white border border-lq-ink-100 rounded-2xl p-5" data-testid="admin-new-country">
            <h2 className="font-display text-lg font-semibold text-lq-ink-900">Add or update a country</h2>
            <p className="text-sm text-lq-ink-500">Slug is auto-generated from the name if blank. Existing slugs are updated.</p>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
                <Field label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="admin-country-name" />
                <Field label="Flag emoji" required value={form.flag_emoji} onChange={(v) => setForm({ ...form, flag_emoji: v })} testid="admin-country-flag" />
                <Field label="Slug (optional)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} testid="admin-country-slug" />
                <Field label="ISO country code" value={form.country_code} onChange={(v) => setForm({ ...form, country_code: v })} testid="admin-country-code" />
                <Field label="Region" value={form.region} onChange={(v) => setForm({ ...form, region: v })} testid="admin-country-region" />
                <label className="block">
                    <span className="text-sm font-medium text-lq-ink-900">Status</span>
                    <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2" data-testid="admin-country-status">
                        <option value="live">Live</option>
                        <option value="coming_soon">Coming soon</option>
                        <option value="archived">Archived</option>
                    </select>
                </label>
                <label className="block">
                    <span className="text-sm font-medium text-lq-ink-900">Quiz status</span>
                    <select value={form.quiz_status} onChange={(e) => setForm({ ...form, quiz_status: e.target.value })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2" data-testid="admin-country-quiz-status">
                        <option value="live">Live (quiz playable)</option>
                        <option value="coming_soon">Coming soon (locked)</option>
                    </select>
                </label>
            </div>
            <div className="mt-3">
                <span className="text-sm font-medium text-lq-ink-900">Languages spoken</span>
                <div className="mt-2 flex flex-wrap gap-2" data-testid="admin-country-langs">
                    {LANGS.map((s) => (
                        <button type="button" key={s} onClick={() => toggleLang(s)} className={`px-3 py-1 rounded-full text-sm border-2 capitalize ${form.language_slugs.includes(s) ? 'bg-lq-purple-500 text-white border-lq-purple-500' : 'bg-white border-lq-ink-100 text-lq-ink-900'}`}>
                            {s}
                        </button>
                    ))}
                </div>
            </div>
            <label className="block mt-3">
                <span className="text-sm font-medium text-lq-ink-900">Short blurb (1–2 sentences)</span>
                <textarea value={form.blurb} onChange={(e) => setForm({ ...form, blurb: e.target.value })} maxLength={500} rows={2} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2" data-testid="admin-country-blurb" />
            </label>
            <button disabled={busy} className="mt-4 px-5 py-2 rounded-full bg-lq-purple-500 text-white font-semibold inline-flex items-center gap-1 hover:bg-lq-purple-700 disabled:opacity-50" data-testid="admin-country-save">
                {busy ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>} Save country
            </button>
        </form>
    );
}

function Field({ label, value, onChange, required, testid }) {
    return (
        <label className="block">
            <span className="text-sm font-medium text-lq-ink-900">{label}{required && <span className="text-lq-error">*</span>}</span>
            <input value={value} onChange={(e) => onChange(e.target.value)} required={required} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2" data-testid={testid} />
        </label>
    );
}

function CountryAdminCard({ c, onChange }) {
    const [busy, setBusy] = useState(false);
    const fileRef = React.useRef(null);

    const upload = async (file) => {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('replace', 'true');
            const { data } = await api.post(`/admin/countries/${c.slug}/import-excel`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success(`${c.name}: ${data.facts_added} facts, ${data.quiz_added} quiz Qs imported`);
            await onChange?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Upload failed');
        } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
    };

    const archive = async () => {
        if (!window.confirm(`Archive ${c.name}? It stops appearing on the public site (data retained).`)) return;
        try {
            await api.delete(`/admin/countries/${c.slug}`);
            toast.success(`${c.name} archived`);
            await onChange?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not archive');
        }
    };

    return (
        <div className="bg-white border border-lq-ink-100 rounded-2xl p-5 flex flex-col" data-testid={`admin-country-${c.slug}`}>
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-3xl">{c.flag_emoji}</div>
                    <h3 className="mt-1 font-display text-lg font-semibold text-lq-ink-900">{c.name}</h3>
                    <p className="text-xs text-lq-ink-500">{c.region || '—'} · {c.status} · quiz {c.quiz_status || 'coming_soon'}</p>
                </div>
                <div className="text-right text-xs text-lq-ink-500">
                    <div>{c.fact_count || 0} facts</div>
                    <div>{c.quiz_question_count || 0} quiz Qs</div>
                </div>
            </div>
            {(c.language_slugs || []).length > 0 && (
                <p className="mt-2 text-xs text-lq-ink-500">Languages: <span className="capitalize">{(c.language_slugs || []).join(', ')}</span></p>
            )}
            <div className="mt-auto pt-4 flex flex-wrap gap-2">
                <input ref={fileRef} type="file" accept=".xlsx" onChange={(e) => upload(e.target.files?.[0])} className="hidden" data-testid={`admin-country-${c.slug}-file`} />
                <button onClick={() => fileRef.current?.click()} disabled={busy} className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full bg-lq-purple-500 text-white text-sm font-semibold hover:bg-lq-purple-700 disabled:opacity-50" data-testid={`admin-country-${c.slug}-upload`}>
                    {busy ? <Loader2 size={12} className="animate-spin"/> : <Upload size={12}/>} Upload Excel
                </button>
                {c.status !== 'archived' && (
                    <button onClick={archive} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-lq-ink-300 text-lq-error text-sm hover:bg-lq-error/5" data-testid={`admin-country-${c.slug}-archive`}>
                        <Trash2 size={12}/> Archive
                    </button>
                )}
            </div>
        </div>
    );
}
